import type {
  AssistantActivity,
  AssistantToolCallState,
} from '../../types/runtime-state';
import type { LucideIcon } from 'lucide-react';
import {
  AppWindowIcon,
  CameraIcon,
  ClockIcon,
  HelpCircleIcon,
  InfoIcon,
  KeyboardIcon,
  ListIcon,
  MonitorIcon,
  MousePointer2Icon,
  MousePointerClickIcon,
  MoveIcon,
  PlayCircleIcon,
  SearchIcon,
  StopCircleIcon,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { AnimatedLogo } from '../animated-logo';
import { ShinyText } from '../shiny-text/shiny-text';
import { assistantActivityText } from '@/utils/assistant-activity';
import { useActivityPresentation } from '@/utils/use-activity-presentation';

const isEmptyAssistantActivity = (value: AssistantActivity | null) => !value;

export const AssistantActivityLine = ({
  activity,
  activeTools = [],
  agentName,
  className,
  resetKey,
}: {
  activity: AssistantActivity | null | undefined;
  activeTools?: AssistantToolCallState[];
  agentName?: string | null;
  className?: string;
  resetKey?: string | number | null;
}) => {
  const renderableActivity = renderableAssistantActivity(activity);
  const pacedActivity = useActivityPresentation(
    renderableActivity,
    assistantActivityDisplayKey(renderableActivity),
    {
      displayStrength: renderableActivity?.displayStrength,
      displayPriority: renderableActivity?.displayPriority,
      displayHoldMs: renderableActivity?.displayHoldMs,
      emptyKey: 'none',
      emptyValue: null,
      resetKey,
      isEmpty: isEmptyAssistantActivity,
    }
  );
  if (!pacedActivity) return null;

  const computerUseTool = selectComputerUseTool(pacedActivity, activeTools);
  const computerUseText = getComputerUseToolSummary(computerUseTool);
  const text =
    computerUseText ??
    assistantActivityText(pacedActivity, {
      showGoal: false,
    });
  if (!text) return null;

  const ComputerUseIcon = getComputerUseToolIcon(computerUseTool);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex min-w-0 items-center gap-2 py-1 text-sm leading-5 text-text-secondary',
        className
      )}
    >
      <div className="flex h-5 w-5 shrink-0 items-center justify-center text-text-secondary">
        {ComputerUseIcon ? (
          <ComputerUseIcon className="size-4" strokeWidth={2} />
        ) : (
          <AnimatedLogo className="h-4 w-5" strokeWidth={4.5} />
        )}
      </div>
      {agentName ? (
        <>
          <span className="max-w-40 shrink-0 truncate font-medium text-text-primary">
            {agentName}
          </span>
          <span className="shrink-0 text-text-tertiary">·</span>
        </>
      ) : null}
      <ShinyText className="min-w-0 flex-1 truncate text-sm text-text-secondary">
        {text}
      </ShinyText>
    </div>
  );
};

function renderableAssistantActivity(
  activity: AssistantActivity | null | undefined
): AssistantActivity | null {
  if (!activity) return null;

  const phase = activity.phase.trim().toLowerCase();
  const status = activity.status?.trim().toLowerCase() ?? '';
  const terminalFailure =
    status === 'failed' ||
    status === 'error' ||
    status === 'cancelled' ||
    status === 'canceled';
  if (phase === 'idle' && !terminalFailure) {
    return null;
  }
  return activity;
}

function assistantActivityDisplayKey(
  activity: AssistantActivity | null
): string {
  if (!activity) return 'none';
  return [
    activity.agentId ?? '',
    activity.sessionId ?? '',
    activity.conversationId ?? '',
    activity.phase,
    activity.status ?? '',
    activity.goal ?? '',
    activity.action ?? '',
    activity.summary ?? '',
    activity.toolCallId ?? '',
    activity.toolName ?? '',
    activity.displayStrength ?? '',
    activity.displayPriority ?? '',
    activity.displayHoldMs ?? '',
  ].join('\u001f');
}

function getComputerUseToolSummary(
  tool: AssistantToolCallState | null | undefined
): string | null {
  if (!isComputerUseTool(tool)) {
    return null;
  }
  const summary = tool.summary?.trim() ?? '';
  return summary.length > 0 ? summary : getComputerUseFallbackSummary(tool);
}

function getComputerUseToolIcon(
  tool: AssistantToolCallState | null | undefined
): LucideIcon | null {
  if (!isComputerUseTool(tool)) {
    return null;
  }

  const action = getComputerUseToolAction(tool);
  switch (action) {
    case 'start':
      return PlayCircleIcon;
    case 'end':
    case 'stop':
      return StopCircleIcon;
    case 'status':
      return InfoIcon;
    case 'mode-help':
      return HelpCircleIcon;
    case 'list-apps':
    case 'list-windows':
      return ListIcon;
    case 'open-app':
    case 'focus':
      return AppWindowIcon;
    case 'get-app-state':
      return SearchIcon;
    case 'click':
    case 'mouse-down':
    case 'mouse-up':
      return MousePointerClickIcon;
    case 'mouse-move':
      return MousePointer2Icon;
    case 'type-text':
    case 'set-value':
    case 'press-key':
    case 'hold-key':
      return KeyboardIcon;
    case 'scroll':
    case 'drag':
    case 'zoom':
      return MoveIcon;
    case 'wait':
      return ClockIcon;
    case 'screenshot':
      return CameraIcon;
    case 'screen-size':
    case 'cursor-position':
    case 'thinking':
      return MonitorIcon;
    default:
      return MonitorIcon;
  }
}

function isComputerUseTool(
  tool: AssistantToolCallState | null | undefined
): tool is AssistantToolCallState {
  const name = tool?.toolName?.trim().toLowerCase();
  return (
    name === 'computer_use' || name === 'computer-use' || name === 'computeruse'
  );
}

function selectComputerUseTool(
  activity: AssistantActivity,
  tools: AssistantToolCallState[]
): AssistantToolCallState | null {
  const computerUseTools = tools.filter(isComputerUseTool);
  if (computerUseTools.length === 0) {
    return null;
  }

  const toolCallId = activity.toolCallId?.trim();
  if (toolCallId) {
    const matched = computerUseTools.find(tool => tool.callId === toolCallId);
    if (matched) {
      return matched;
    }
    return null;
  }

  if (isComputerUseActivity(activity)) {
    return computerUseTools[computerUseTools.length - 1] ?? null;
  }

  if (computerUseTools.length === 1 && isGenericMacControlActivity(activity)) {
    return computerUseTools[0];
  }

  return null;
}

function isComputerUseActivity(activity: AssistantActivity): boolean {
  const name = activity.toolName?.trim().toLowerCase();
  return (
    name === 'computer_use' || name === 'computer-use' || name === 'computeruse'
  );
}

function isGenericMacControlActivity(activity: AssistantActivity): boolean {
  const text = `${activity.action ?? ''} ${activity.summary ?? ''}`
    .trim()
    .toLowerCase();
  return (
    text === 'mac control' ||
    text === 'computer use' ||
    text === 'computeruse' ||
    text.includes('mac control')
  );
}

function getComputerUseToolAction(
  tool: AssistantToolCallState | null | undefined
): string | null {
  if (!tool?.args) {
    return null;
  }
  try {
    const payload = JSON.parse(tool.args) as { action?: unknown };
    return typeof payload.action === 'string'
      ? payload.action.trim().toLowerCase()
      : null;
  } catch {
    return null;
  }
}

function getComputerUseFallbackSummary(
  tool: AssistantToolCallState
): string | null {
  const action = getComputerUseToolAction(tool);
  switch (action) {
    case 'start':
      return 'Start controlling this Mac';
    case 'end':
    case 'stop':
      return 'Stop controlling this Mac';
    case 'status':
      return 'Check ComputerUse status';
    case 'mode-help':
      return 'Show ComputerUse help';
    case 'list-apps':
      return 'List Mac apps';
    case 'open-app':
      return 'Open app';
    case 'list-windows':
      return 'List app windows';
    case 'get-app-state':
      return 'Inspect app';
    case 'click':
      return 'Click';
    case 'mouse-down':
      return 'Press mouse';
    case 'mouse-up':
      return 'Release mouse';
    case 'mouse-move':
      return 'Move pointer';
    case 'type-text':
      return 'Type text';
    case 'set-value':
      return 'Set value';
    case 'press-key':
      return 'Press key';
    case 'hold-key':
      return 'Hold key';
    case 'scroll':
      return 'Scroll';
    case 'perform-secondary-action':
      return 'Perform UI action';
    case 'drag':
      return 'Drag';
    case 'wait':
      return 'Wait';
    case 'screenshot':
      return 'Take screenshot';
    case 'screen-size':
      return 'Read screen size';
    case 'cursor-position':
      return 'Read pointer position';
    case 'focus':
      return 'Focus app';
    case 'zoom':
      return 'Zoom into screen';
    case 'thinking':
      return 'Observe screen';
    default:
      return action ? humanizeComputerUseAction(action) : 'Use this Mac';
  }
}

function humanizeComputerUseAction(action: string): string {
  const words = action
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return 'Use this Mac';
  }
  return words
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
}
